import classNames from 'classnames';
import _merge from 'lodash/merge';
import React, {ReactNode, useState} from 'react';
import Modal, {OnAfterOpenCallbackOptions, Styles} from 'react-modal';
import styles from './CLModal.module.scss';
import CLModalHeader from './Header/CLModalHeader';
import useModalDrag from './hooks/useModalDrag';

Modal.setAppElement(document.body);

/**
 * documentation: https://reactcommunity.org/react-modal/
 */

export const MODAL_Z_INDEX = 2000;

const modalStyle: Modal.Styles = {
  overlay: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0, 0,0, 0.5)',
    zIndex: MODAL_Z_INDEX,
  },
  content: {
    border: 'var(--cl_border_component)',
    background: 'var(--cl_color_modal_bg_body)',
    overflow: 'hidden',
    WebkitOverflowScrolling: 'touch',
    borderRadius: 'var(--cl_border_radius)',
    outline: 'none',
    boxShadow: 'var(--cl_modal_box_shadow_component)',
  },
};

export interface CLModalProps extends Omit<Modal.Props, 'isOpen' | 'children'> {
  isOpen?: boolean;
  title?: ReactNode;
  modalFooter?: ReactNode;
  modalStyle?: Styles;
  children?: ReactNode;
  width?: string;
  bodyClassName?: string;
  onCancel?: () => void;
  allowPropagation?: boolean;
  isDraggable?: boolean;
  hideModalHeader?: boolean;
  qaHook?: string;
}

export default function CLModal({
  className,
  bodyClassName = '',
  children,
  isOpen,
  modalFooter,
  allowPropagation,
  title = '',
  modalStyle: styleOverrides = {},
  width,
  onCancel = () => {},
  style: styleProp,
  isDraggable,
  hideModalHeader = false,
  qaHook = '',
  ...otherProps
}: CLModalProps) {
  const [isOpened, setIsOpened] = useState<boolean>(false);
  const modalWidth = width || modalStyle?.content?.width || 'auto';
  const {handleDragStart, modalPositionStyle} = useModalDrag(width);
  const style = _merge(
    {
      overlay: {...modalStyle.overlay, ...styleProp?.overlay},
      content: {
        ...modalStyle.content,
        ...styleProp?.content,
        ...(isDraggable ? modalPositionStyle : {}),
        width: modalWidth,
      },
    },
    styleOverrides,
  );

  function preventEventPropagation(event: React.MouseEvent) {
    !allowPropagation && event.stopPropagation();
  }

  function cancelOnEscape(event: KeyboardEvent) {
    if (event.key === 'Escape') {
      onCancel();
    }
  }

  return (
    <Modal
      overlayClassName={classNames(styles.cl_modal_overlay, {
        [styles.cl_modal_overlay_open]: isOpened,
      })}
      isOpen={isOpen}
      shouldCloseOnOverlayClick={false}
      className={classNames(styles.clmodal_content, className)}
      portalClassName={styles.common_clmodal}
      style={style}
      onAfterClose={() => setIsOpened(false)}
      onAfterOpen={({
        overlayEl: backgroundElement,
        contentEl: modalElement,
      }: OnAfterOpenCallbackOptions) => {
        function preventBackgroundEvent(event: Event) {
          if (!event.composedPath().includes(modalElement)) {
            event.stopPropagation();
          }
        }

        setIsOpened(true);

        backgroundElement.addEventListener('mousedown', preventBackgroundEvent);
        backgroundElement.addEventListener('wheel', preventBackgroundEvent);
        backgroundElement.addEventListener('keydown', cancelOnEscape);
      }}
      shouldFocusAfterRender={true}
      {...otherProps}
    >
      {hideModalHeader ? null : (
        <CLModalHeader
          title={title}
          onClose={onCancel}
          onDrag={isDraggable ? handleDragStart : null}
          qaHook={qaHook}
        />
      )}
      {children && (
        <div className={styles.modal_display_content} onClick={preventEventPropagation}>
          <div
            className={classNames(styles.modal_body, bodyClassName)}
            onMouseDown={preventEventPropagation}
            onWheel={preventEventPropagation}
          >
            {children}
          </div>
        </div>
      )}
      {modalFooter && <div className={styles.modal_footer}>{modalFooter}</div>}
    </Modal>
  );
}
